import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconFile,
  IconHeadset,
  IconMessageCircle,
  IconPaperclip,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  'pdf', 'png', 'jpg', 'jpeg', 'webp',
  'doc', 'docx', 'xls', 'xlsx', 'txt',
];

const initialForm = {
  title: '',
  description: '',
  priority: 'Orta',
  device_id: '',
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

function StatCard({ title, value, subtitle, icon, color, background }) {
  return (
    <Paper
      withBorder
      radius="xl"
      p="lg"
      style={{ borderColor: '#e9ecef', boxShadow: '0 4px 16px rgba(0,0,0,.035)' }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={3}>
          <Text c="dimmed" size="sm" fw={500}>{title}</Text>
          <Text fw={800} size="28px" lh={1.15}>{value}</Text>
          <Text c="dimmed" size="xs">{subtitle}</Text>
        </Stack>
        <ThemeIcon size={44} radius="xl" color={color} variant="light" style={{ background }}>
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

export default function ITSupportPage() {
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [newTicketOpened, setNewTicketOpened] = useState(false);
  const [detailDrawerOpened, setDetailDrawerOpened] = useState(false);
  const [ticketForm, setTicketForm] = useState(initialForm);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const priorityColor = (priority) => ({
    Düşük: 'gray', Orta: 'blue', Yüksek: 'orange', Kritik: 'red',
  }[priority] || 'blue');

  const statusColor = (status) => ({
    Açık: 'red', İşlemde: 'yellow', Çözüldü: 'teal', Kapatıldı: 'gray',
  }[status] || 'blue');

  const fetchOverview = async () => {
    try {
      const res = await api.get('/it-support/overview');
      setOverview(res.data);
    } catch (err) {
      console.error('Özet veriler alınamadı', err);
    }
  };

  const fetchMyTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/it-support/tickets/me');
      setMyTickets(res.data || []);
    } catch (err) {
      console.error('Kendi taleplerim alınamadı', err);
      notifications.show({ title: 'Hata', message: 'Destek talepleri alınamadı.', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([fetchOverview(), fetchMyTickets()]);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const valid = [];
    const errors = [];

    incoming.forEach((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        errors.push(`${file.name}: desteklenmeyen dosya türü`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: 10 MB sınırını aşıyor`);
        return;
      }
      const duplicate = [...selectedFiles, ...valid].some(
        (item) => item.name === file.name && item.size === file.size
      );
      if (!duplicate) valid.push(file);
    });

    const remaining = MAX_FILES - selectedFiles.length;
    if (valid.length > remaining) {
      errors.push(`En fazla ${MAX_FILES} dosya ekleyebilirsiniz.`);
    }

    setSelectedFiles((prev) => [...prev, ...valid.slice(0, remaining)]);

    if (errors.length) {
      notifications.show({
        title: 'Bazı dosyalar eklenmedi',
        message: errors[0],
        color: 'orange',
      });
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const closeNewTicket = () => {
    if (submitting) return;
    setNewTicketOpened(false);
    setTicketForm(initialForm);
    setSelectedFiles([]);
  };

  const handleCreateTicket = async () => {
    if (!ticketForm.title.trim() || !ticketForm.description.trim()) {
      notifications.show({
        title: 'Eksik bilgi',
        message: 'Konu ve açıklama alanlarını doldurun.',
        color: 'yellow',
      });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', ticketForm.title.trim());
      formData.append('description', ticketForm.description.trim());
      formData.append('priority', ticketForm.priority);
      if (ticketForm.device_id) formData.append('device_id', ticketForm.device_id);
      selectedFiles.forEach((file) => formData.append('files', file));

      // Content-Type'ı elle verme; multipart boundary'yi browser oluşturur.
      await api.post('/it-support/tickets', formData);

      notifications.show({
        title: 'Talebiniz oluşturuldu',
        message: 'BT ekibi talebinizi incelemeye aldı.',
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
      setNewTicketOpened(false);
      setTicketForm(initialForm);
      setSelectedFiles([]);
      await refreshAll();
    } catch (err) {
      notifications.show({
        title: 'Talep oluşturulamadı',
        message: err.response?.data?.detail || 'Lütfen tekrar deneyin.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openTicketDetail = async (ticketId) => {
    try {
      const res = await api.get(`/it-support/tickets/${ticketId}`);
      setSelectedTicket(res.data);
      setDetailDrawerOpened(true);
    } catch (err) {
      notifications.show({ title: 'Hata', message: 'Talep detayı alınamadı.', color: 'red' });
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedTicket) return;
    setCommentLoading(true);
    try {
      const res = await api.post(`/it-support/tickets/${selectedTicket.id}/comments`, {
        comment: commentText.trim(),
      });
      setSelectedTicket(res.data);
      setCommentText('');
      notifications.show({
        title: 'Mesaj gönderildi',
        message: 'Yorumunuz destek kaydına eklendi.',
        color: 'teal',
      });
      await refreshAll();
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: err.response?.data?.detail || 'Yorum eklenemedi.',
        color: 'red',
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const filteredTickets = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    return myTickets.filter((ticket) => {
      const matchesSearch = !term
        || String(ticket.id).includes(term)
        || ticket.title?.toLocaleLowerCase('tr-TR').includes(term);
      const matchesStatus = !statusFilter || ticket.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [myTickets, search, statusFilter]);

  return (
    <Stack gap="xl">
      <Paper
        radius="xl"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          background: 'linear-gradient(135deg, #0b5ed7 0%, #1677ff 58%, #4c9aff 100%)',
          color: 'white',
          boxShadow: '0 14px 35px rgba(22,119,255,.18)',
        }}
      >
        <Group justify="space-between" align="center" gap="lg">
          <Group gap="md" wrap="nowrap">
            <ThemeIcon size={54} radius="xl" color="white" variant="white">
              <IconHeadset size={28} color="#1677ff" />
            </ThemeIcon>
            <div>
              <Title order={2} c="white">BT Destek Merkezi</Title>
              <Text c="rgba(255,255,255,.82)" mt={4}>
                Teknik sorunlarınızı bildirin, süreci tek ekrandan takip edin.
              </Text>
            </div>
          </Group>
          <Group>
            <Tooltip label="Verileri yenile">
              <ActionIcon
                size="lg"
                radius="md"
                variant="white"
                color="blue"
                onClick={refreshAll}
                aria-label="Yenile"
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button
              size="md"
              radius="md"
              color="dark"
              leftSection={<IconPlus size={18} />}
              onClick={() => setNewTicketOpened(true)}
            >
              Yeni Destek Talebi
            </Button>
          </Group>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }}>
        <StatCard
          title="Açık Taleplerim"
          value={overview?.my_open_tickets ?? 0}
          subtitle="Aksiyon bekleyen kayıtlar"
          color="red"
          background="#fff5f5"
          icon={<IconAlertCircle size={22} />}
        />
        <StatCard
          title="Toplam Taleplerim"
          value={overview?.my_total_tickets ?? 0}
          subtitle="Oluşturduğunuz tüm kayıtlar"
          color="blue"
          background="#e7f5ff"
          icon={<IconMessageCircle size={22} />}
        />
        <StatCard
          title="İşlemde"
          value={myTickets.filter((t) => t.status === 'İşlemde').length}
          subtitle="BT ekibinin üzerinde çalıştıkları"
          color="orange"
          background="#fff4e6"
          icon={<IconClock size={22} />}
        />
        <StatCard
          title="Çözülenler"
          value={myTickets.filter((t) => ['Çözüldü', 'Kapatıldı'].includes(t.status)).length}
          subtitle="Tamamlanan destek kayıtları"
          color="teal"
          background="#e6fcf5"
          icon={<IconCheck size={22} />}
        />
      </SimpleGrid>

      <Paper
        withBorder
        radius="xl"
        style={{ overflow: 'hidden', borderColor: '#e9ecef', boxShadow: '0 4px 20px rgba(0,0,0,.03)' }}
      >
        <Group justify="space-between" p="lg" align="flex-end">
          <div>
            <Title order={4}>Destek Taleplerim</Title>
            <Text size="sm" c="dimmed" mt={3}>Geçmiş ve devam eden destek kayıtlarınız</Text>
          </div>
          <Group gap="sm">
            <TextInput
              placeholder="Talep no veya konu ara..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={250}
            />
            <Select
              placeholder="Durum"
              clearable
              data={['Açık', 'İşlemde', 'Çözüldü', 'Kapatıldı']}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || '')}
              w={150}
            />
          </Group>
        </Group>
        <Divider />

        {loading ? (
          <Group justify="center" p={50}><Loader size="sm" /></Group>
        ) : filteredTickets.length === 0 ? (
          <Stack align="center" gap={8} py={54} px="md">
            <ThemeIcon size={48} radius="xl" color="gray" variant="light">
              <IconMessageCircle size={24} />
            </ThemeIcon>
            <Text fw={600}>Gösterilecek destek talebi bulunamadı</Text>
            <Text c="dimmed" size="sm">Arama kriterlerini değiştirin veya yeni bir talep oluşturun.</Text>
          </Stack>
        ) : (
          <Table highlightOnHover verticalSpacing="md" horizontalSpacing="lg">
            <Table.Thead>
              <Table.Tr style={{ background: '#f8f9fa' }}>
                <Table.Th>TALEP NO</Table.Th>
                <Table.Th>KONU</Table.Th>
                <Table.Th>ÖNCELİK</Table.Th>
                <Table.Th>DURUM</Table.Th>
                <Table.Th>TARİH</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredTickets.map((ticket) => (
                <Table.Tr
                  key={ticket.id}
                  onClick={() => openTicketDetail(ticket.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td><Text size="sm" fw={700}>#{ticket.id}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600} lineClamp={1}>{ticket.title}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={priorityColor(ticket.priority)}>{ticket.priority}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="dot" color={statusColor(ticket.status)}>{ticket.status}</Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{formatDate(ticket.created_at)}</Text></Table.Td>
                  <Table.Td>
                    <ActionIcon variant="subtle" color="gray" aria-label="Detayı görüntüle">
                      <IconChevronRight size={18} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer
        opened={detailDrawerOpened}
        onClose={() => setDetailDrawerOpened(false)}
        title={<Text fw={700}>Destek Talebi Detayı</Text>}
        position="right"
        size="lg"
        padding="xl"
      >
        {selectedTicket && (
          <Stack gap="lg">
            <Card withBorder radius="lg" p="lg">
              <Group justify="space-between" mb="xs">
                <Text size="xs" fw={700} c="dimmed">TALEP #{selectedTicket.id}</Text>
                <Text size="xs" c="dimmed">{formatDate(selectedTicket.created_at)}</Text>
              </Group>
              <Text fw={700} size="lg">{selectedTicket.title}</Text>
              <Group mt="sm" gap="xs">
                <Badge variant="light" color={priorityColor(selectedTicket.priority)}>{selectedTicket.priority}</Badge>
                <Badge variant="dot" color={statusColor(selectedTicket.status)}>{selectedTicket.status}</Badge>
              </Group>
              <Divider my="md" />
              <Text size="xs" fw={700} c="dimmed" mb={6}>AÇIKLAMA</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description}</Text>
            </Card>

            <Card withBorder radius="lg" p="lg">
              <Group justify="space-between" mb="md">
                <Title order={5}>Yazışmalar</Title>
                <Badge variant="light" color="gray">{selectedTicket.comments?.length || 0}</Badge>
              </Group>
              <Stack gap="sm">
                {selectedTicket.comments?.length ? selectedTicket.comments.map((comment) => (
                  <Paper key={comment.id} bg="#f8f9fa" radius="md" p="sm">
                    <Group justify="space-between" mb={4}>
                      <Text size="xs" fw={700}>{comment.user?.username || 'Kullanıcı'}</Text>
                      <Text size="xs" c="dimmed">{formatDate(comment.created_at)}</Text>
                    </Group>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{comment.comment}</Text>
                  </Paper>
                )) : (
                  <Text size="sm" c="dimmed">Henüz bir yazışma bulunmuyor.</Text>
                )}

                <Textarea
                  mt="sm"
                  minRows={3}
                  autosize
                  maxRows={7}
                  placeholder="BT ekibine mesajınızı yazın..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.currentTarget.value)}
                />
                <Group justify="flex-end">
                  <Button
                    loading={commentLoading}
                    disabled={!commentText.trim()}
                    onClick={handleAddComment}
                  >
                    Mesaj Gönder
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        )}
      </Drawer>

      <Modal
        opened={newTicketOpened}
        onClose={closeNewTicket}
        title={
          <Group gap="sm">
            <ThemeIcon variant="light" radius="md"><IconHeadset size={18} /></ThemeIcon>
            <div>
              <Text fw={700}>Yeni Destek Talebi</Text>
              <Text size="xs" c="dimmed" fw={400}>Sorunu mümkün olduğunca detaylı tarif edin.</Text>
            </div>
          </Group>
        }
        size="lg"
        radius="lg"
        centered
      >
        <Stack gap="md" mt="sm">
          <TextInput
            label="Konu"
            placeholder="Örn. Yazıcıdan çıktı alamıyorum"
            required
            value={ticketForm.title}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setTicketForm((prev) => ({ ...prev, title: value }));
            }}
          />

          <Select
            label="Öncelik"
            description="İşinizi ne ölçüde etkilediğini seçin."
            data={[
              { value: 'Düşük', label: 'Düşük — İşimi engellemiyor' },
              { value: 'Orta', label: 'Orta — Çalışmamı etkiliyor' },
              { value: 'Yüksek', label: 'Yüksek — İşimi ciddi etkiliyor' },
              { value: 'Kritik', label: 'Kritik — Çalışamıyorum' },
            ]}
            value={ticketForm.priority}
            onChange={(value) => setTicketForm((prev) => ({ ...prev, priority: value || 'Orta' }))}
          />

          <Textarea
            label="Açıklama"
            description="Hata mesajı, ne zaman başladığı ve denediğiniz adımları yazabilirsiniz."
            placeholder="Sorunu detaylı şekilde açıklayın..."
            minRows={5}
            autosize
            maxRows={10}
            required
            value={ticketForm.description}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setTicketForm((prev) => ({ ...prev, description: value }));
            }}
          />

          <Box>
            <Group gap={5} mb={5}>
              <Text size="sm" fw={500}>Dosya ekleri</Text>
              <Text size="xs" c="dimmed">(isteğe bağlı)</Text>
            </Group>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <Paper
              withBorder
              radius="lg"
              p="lg"
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                addFiles(e.dataTransfer.files);
              }}
              style={{
                cursor: 'pointer',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: dragActive ? '#228be6' : '#ced4da',
                background: dragActive ? '#f1f8ff' : '#fafbfc',
                transition: 'all .15s ease',
              }}
            >
              <Stack align="center" gap={6}>
                <ThemeIcon size={42} radius="xl" variant="light">
                  <IconUpload size={20} />
                </ThemeIcon>
                <Text size="sm" fw={600}>Dosyaları buraya sürükleyin veya seçmek için tıklayın</Text>
                <Text size="xs" c="dimmed" ta="center">
                  PDF, Word, Excel, TXT veya görsel • En fazla 5 dosya • Dosya başına 10 MB
                </Text>
              </Stack>
            </Paper>

            {selectedFiles.length > 0 && (
              <Stack gap="xs" mt="sm">
                {selectedFiles.map((file, index) => (
                  <Paper key={`${file.name}-${file.size}`} withBorder radius="md" p="sm">
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                        <ThemeIcon color="gray" variant="light" radius="md">
                          <IconFile size={17} />
                        </ThemeIcon>
                        <div style={{ minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate>{file.name}</Text>
                          <Text size="xs" c="dimmed">{formatBytes(file.size)}</Text>
                        </div>
                      </Group>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                        aria-label="Dosyayı kaldır"
                      >
                        <IconX size={17} />
                      </ActionIcon>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />
          <Group justify="space-between">
            <Group gap={6}>
              <IconPaperclip size={15} color="#868e96" />
              <Text size="xs" c="dimmed">{selectedFiles.length}/{MAX_FILES} dosya seçildi</Text>
            </Group>
            <Group gap="sm">
              <Button variant="default" onClick={closeNewTicket}>Vazgeç</Button>
              <Button
                leftSection={<IconPlus size={17} />}
                onClick={handleCreateTicket}
                loading={submitting}
              >
                Talebi Oluştur
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
